import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ModalProps, ModalState, ModalVariant } from './types';

// ─── Constants ────────────────────────────────────────────────────────────────

const VARIANT_MAX_WIDTH: Record<ModalVariant, string> = {
  default: 'sm:max-w-[480px]',
  wide: 'sm:max-w-[640px]',
  xl: 'sm:max-w-[75vw]',
  fullscreen: '',
};

const PANEL_BASE =
  'relative flex flex-col bg-white dark:bg-neutral-900 shadow-lg outline-none w-full';

// ─── Focus trap ───────────────────────────────────────────────────────────────

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),' +
  'select:not([disabled]),[tabindex]:not([tabindex="-1"])';

function trapFocus(panelEl: HTMLElement, event: KeyboardEvent) {
  const focusable = Array.from(panelEl.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null,
  );
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey) {
    if (document.activeElement === first) {
      event.preventDefault();
      last.focus();
    }
  } else {
    if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export function Modal({
  open,
  onClose,
  title,
  variant = 'default',
  children,
  footer,
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  className,
  allowOverflow = false,
}: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const [state, setState] = useState<ModalState>('closed');

  // Track open → opening → open, open → closing → closed transitions
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      setState('opening');
      const t = setTimeout(() => setState('open'), 16); // next paint
      return () => clearTimeout(t);
    } else {
      if (state === 'closed') return;
      setState('closing');
      const t = setTimeout(() => {
        setState('closed');
        // Return focus to trigger element
        if (triggerRef.current && 'focus' in triggerRef.current) {
          (triggerRef.current as HTMLElement).focus();
        }
      }, 150);
      return () => clearTimeout(t);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-focus first focusable element when open
  useEffect(() => {
    if (state === 'open' && panelRef.current) {
      const focusable = panelRef.current.querySelector<HTMLElement>(FOCUSABLE);
      focusable ? focusable.focus() : panelRef.current.focus();
    }
  }, [state]);

  // Keyboard handling
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape' && closeOnEscape) {
        onClose();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        trapFocus(panelRef.current, e.nativeEvent);
      }
    },
    [closeOnEscape, onClose],
  );

  const handleBackdropClick = useCallback(() => {
    if (closeOnBackdrop) onClose();
  }, [closeOnBackdrop, onClose]);

  if (state === 'closed') return null;

  const isFullscreen = variant === 'fullscreen';

  // Backdrop opacity
  const backdropOpacity =
    state === 'opening' ? 'opacity-0' : state === 'closing' ? 'opacity-0' : 'opacity-100';

  // Panel transform/opacity
  const panelVisible = state === 'open';
  const panelTransform = panelVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95';

  // Panel sizing
  const panelSizing = isFullscreen
    ? 'w-full h-full rounded-none'
    : `rounded-lg ${VARIANT_MAX_WIDTH[variant]} sm:mx-auto mx-0 w-full max-h-[90vh]`;

  // Container alignment
  const containerAlign = isFullscreen ? 'items-start justify-start' : 'items-center justify-center p-4 sm:p-6';

  return (
    <div
      className="fixed inset-0 z-[40] overflow-y-auto"
      aria-hidden={!open}
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity duration-200 ${backdropOpacity}`}
        aria-hidden="true"
        onClick={handleBackdropClick}
      />

      {/* Centering container */}
      <div className={`relative flex min-h-full ${containerAlign}`}>
        {/* Panel */}
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          tabIndex={-1}
          className={[
            PANEL_BASE,
            panelSizing,
            `transition-all duration-200 ${panelTransform}`,
            'z-[50]',
            className ?? '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between gap-4 border-b border-neutral-200 dark:border-neutral-700 px-6 py-4 shrink-0">
              {title && (
                <h2
                  id={titleId}
                  className="text-base font-semibold text-neutral-900 dark:text-neutral-100 truncate"
                >
                  {title}
                </h2>
              )}
              {showCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  className="ml-auto flex items-center justify-center rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  aria-label="Close"
                >
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Body */}
          <div className={`flex-1 px-6 py-4 ${allowOverflow ? 'overflow-visible' : 'overflow-y-auto'}`}>{children}</div>

          {/* Footer */}
          {footer && (
            <div className="flex items-center justify-end gap-3 border-t border-neutral-200 dark:border-neutral-700 px-6 py-4 shrink-0">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
